// ─────────────────────────────────────────────────────────────────────────────
// HostBridgeService — the app's side of the embedded AI assistant conversation.
//
// The assistant runs in an iframe and cannot see this app: not which screen is
// open, not whether there is a half-filled form on it. So it reports facts and
// this service decides what they mean. Three kinds arrive, handled differently
// on purpose:
//
//   scope.changed    the assistant switched project. Adopted unconditionally —
//                    two panes disagreeing about the active project makes every
//                    later action on either side target the wrong one. Only
//                    navigates when the screen you are on belongs to the project
//                    you just left, because that screen is now showing you the
//                    wrong thing.
//   entity.changed   something was written. Published on entityChanged$ so open
//                    screens can refresh in place. Navigates only if the user
//                    turned on follow mode.
//   place.requested  the user clicked an "open" chip in the chat. Always
//                    navigates: they just asked.
//
// The assistant never sends a route. Deciding where to go stays here.
// ─────────────────────────────────────────────────────────────────────────────
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, groupBy, mergeMap } from 'rxjs/operators';
import { AppConstants } from 'src/app/utilities/constant';
import { ToastService } from 'src/app/services/toast.service';

export type HostEntity =
  | 'project'
  | 'service'
  | 'machine'
  | 'roadmap'
  | 'hazard'
  | 'checklist'
  | 'report';

export type HostAction = 'created' | 'updated' | 'deleted';

export interface HostEntityRef {
  entity: HostEntity;
  id?: string;
  name?: string;
  projectId?: string;
  serviceId?: string;
  machineId?: string;
}

export type EntityChangedEvent = HostEntityRef & { action: HostAction };

/**
 * What a screen does when the assistant asks to open something it owns.
 *
 * Return true if you dealt with it. Return false to decline — the bridge then
 * falls back to routing, so declining is how a page that is open but showing a
 * different project hands the request on rather than swallowing it.
 */
export type OpenHandler = (ref: HostEntityRef) => boolean | Promise<boolean>;

export interface ScopeChangedEvent {
  projectId: string;
  projectName?: string;
}

type HostEventPayload =
  | (EntityChangedEvent & { event: 'entity.changed' })
  | (HostEntityRef & { event: 'place.requested' })
  | (ScopeChangedEvent & { event: 'scope.changed' });

interface HostMessage {
  type: 'BLUEPRINT_EVENT';
  version: number;
  payload: HostEventPayload;
}

const FOLLOW_MODE_STORAGE_KEY = 'bp_assistant_follow_mode';

/** Collapses a burst of writes about one entity into a single refresh. Answering
 *  a checklist reports once per step, and a full risk assessment runs to 60-90 of
 *  them. */
const REFRESH_DEBOUNCE_MS = 400;

/** Entities the project detail screen shows. Everything under a project is read
 *  from that one layout, so a write to any of them makes it stale. */
const PROJECT_LAYOUT_ENTITIES: HostEntity[] = ['service', 'machine', 'roadmap'];

/** Toast text for a write, e.g. `Assistant created machine "Press 3"`. */
function describeChange(event: EntityChangedEvent): string {
  const what = event.name ? `${event.entity} "${event.name}"` : event.entity;
  return `Assistant ${event.action} ${what}`;
}

@Injectable({ providedIn: 'root' })
export class HostBridgeService {
  /**
   * Writes the assistant made, one event per entity per burst. Subscribe from a
   * page to re-fetch what it is showing; check the entity and ids first, since
   * every page receives every event.
   */
  public readonly entityChanged$: Observable<EntityChangedEvent>;

  /** The project the assistant is now working in. Already applied by this
   *  service — exposed for screens that cache a project id of their own. */
  public readonly scopeChanged$ = new Subject<ScopeChangedEvent>();

  /** Off by default: the assistant refreshes what you are looking at, it does
   *  not drag you around. Flip it on for a bulk session where following along is
   *  the point. */
  public readonly followMode$ = new BehaviorSubject<boolean>(false);

  /** Last project the assistant announced, or the one this app pushed into it. */
  public activeProjectId = '';

  private readonly rawEntityChanged = new Subject<EntityChangedEvent>();
  private assistantOrigin = '';
  private assistantWindow: Window | null = null;
  private listener: ((event: MessageEvent) => void) | null = null;

  /** Open handlers, by entity. See `register`. */
  private readonly handlers = new Map<HostEntity, OpenHandler>();

  /** An open request that arrived with no screen able to serve it. Held across
   *  the navigation it triggers, then replayed once that screen registers. */
  private pending: HostEntityRef | null = null;

  constructor(
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly toastService: ToastService
  ) {
    this.entityChanged$ = this.rawEntityChanged.pipe(
      groupBy((e) => `${e.entity}:${e.id || e.machineId || e.projectId || ''}`),
      mergeMap((group) => group.pipe(debounceTime(REFRESH_DEBOUNCE_MS)))
    );
    this.followMode$.next(localStorage.getItem(FOLLOW_MODE_STORAGE_KEY) === 'true');

    // `reloadProjectLayout` is how this app already tells the project screen to
    // re-fetch after a machine edit. Reusing it means the assistant's writes
    // refresh that screen without any page having to know the assistant exists.
    //
    // The toast is not decoration. Most of what the assistant does is invisible
    // from wherever you happen to be standing — a machine added to a project
    // whose screen is not open changes nothing you can see — and an action that
    // leaves no trace is indistinguishable from one that silently failed.
    this.entityChanged$.subscribe((event) => {
      if (PROJECT_LAYOUT_ENTITIES.indexOf(event.entity) !== -1) {
        document.dispatchEvent(
          new CustomEvent('reloadProjectLayout', { detail: 'assistant' })
        );
      }
      this.announce(describeChange(event));
    });
  }

  /**
   * Starts listening for events from the assistant frame. `origin` is the only
   * origin accepted, and it is also the only one this service will post back to.
   */
  public connect(frame: HTMLIFrameElement, origin: string): void {
    this.disconnect();
    this.assistantWindow = frame.contentWindow;
    this.assistantOrigin = origin;

    // postMessage fires outside Angular's zone, so navigation and any subscriber
    // that touches a template would not trigger change detection on their own.
    this.listener = (event: MessageEvent) => this.zone.run(() => this.handle(event));
    window.addEventListener('message', this.listener);
  }

  public disconnect(): void {
    if (this.listener) {
      window.removeEventListener('message', this.listener);
      this.listener = null;
    }
    this.assistantWindow = null;
  }

  /**
   * Declares that this screen can open `entity` itself, without a navigation.
   *
   * This is how the bridge avoids knowing what any of these things are. A
   * service is a dropdown on one page, a machine needs a record out of that
   * page's loaded list — neither is a route, and neither is this service's
   * business. The page that owns the state says so, and answers from it.
   *
   * Registration doubles as the answer to "is that screen even open?", which
   * is the question a URL cannot honestly answer: the four-eyes routes are
   * nested under /project-detail/:id, so a project id in the URL does not mean
   * the project detail page is mounted. A registered handler does.
   *
   * Call from ngOnInit and keep the returned function for ngOnDestroy.
   */
  public register(entity: HostEntity, handler: OpenHandler): () => void {
    this.handlers.set(entity, handler);
    // This page may be what the bridge was navigating to when it parked a
    // request it could not serve. Hand it over now that someone can take it.
    if (this.pending && this.pending.entity === entity) {
      const ref = this.pending;
      this.pending = null;
      // After the current change-detection pass: a handler registering in
      // ngOnInit usually still has fetches in flight behind it.
      setTimeout(() => this.dispatch(ref, false), 0);
    }
    return () => {
      if (this.handlers.get(entity) === handler) {
        this.handlers.delete(entity);
      }
    };
  }

  public setFollowMode(enabled: boolean): void {
    localStorage.setItem(FOLLOW_MODE_STORAGE_KEY, String(enabled));
    this.followMode$.next(enabled);
  }

  public toggleFollowMode(): void {
    this.setFollowMode(!this.followMode$.value);
  }

  /**
   * Tells the assistant which project this app is on. Making this app the source
   * of truth for scope is what stops the two sides drifting apart: the assistant
   * adopts what it is told rather than keeping a project id of its own.
   */
  public pushProject(projectId: string): void {
    if (!projectId || !this.assistantWindow) {
      return;
    }
    this.activeProjectId = projectId;
    this.assistantWindow.postMessage({ type: 'SET_PROJECT', projectId }, this.assistantOrigin);
  }

  private handle(event: MessageEvent): void {
    if (event.origin !== this.assistantOrigin) {
      return;
    }
    const message = event.data as HostMessage;
    if (!message || message.type !== 'BLUEPRINT_EVENT' || !message.payload) {
      return;
    }

    const payload = message.payload;
    // Most events resolve to a refresh or to nothing at all, so without this
    // there is no way to tell a working bridge from a dead one.
    console.debug('[assistant]', payload.event, payload);

    switch (payload.event) {
      case 'scope.changed':
        this.applyScope(payload);
        return;
      case 'entity.changed':
        this.applyEntityChange(payload);
        return;
      case 'place.requested':
        this.navigateTo(payload);
        return;
    }
  }

  private applyScope(event: ScopeChangedEvent): void {
    if (!event.projectId || event.projectId === this.activeProjectId) {
      return;
    }
    this.activeProjectId = event.projectId;
    this.scopeChanged$.next(event);
    this.announce(`Assistant switched to ${event.projectName || event.projectId}`);

    // Only move the user when staying put would leave a project screen showing
    // the project they just left. Asking the handler settles both halves of
    // that: no project screen mounted means nobody registered, and a mounted
    // one compares against the project id it actually loaded rather than
    // whatever id happens to appear in the URL — the four-eyes routes are
    // nested under /project-detail/:id and would otherwise read as a match.
    const handler = this.handlers.get('project');
    if (!handler) {
      return;
    }
    void Promise.resolve(handler({ entity: 'project', id: event.projectId, name: event.projectName })).then((handled) => {
      const route = handled ? null : this.routeFor({ entity: 'project', id: event.projectId });
      if (route) {
        this.router.navigate(route);
      }
    });
  }

  private applyEntityChange(event: EntityChangedEvent): void {
    this.rawEntityChanged.next(event);
    if (this.followMode$.value) {
      this.navigateTo(event);
    }
  }

  /**
   * Gives an open request to whoever can serve it: the screen that owns the
   * entity if one is mounted, otherwise a navigation towards it.
   *
   * `allowDefer` is false on a replay, so a handler that declines twice ends
   * the attempt instead of looping through the same navigation again.
   */
  private async dispatch(ref: HostEntityRef, allowDefer = true): Promise<void> {
    const handler = this.handlers.get(ref.entity);
    if (handler && (await handler(ref))) {
      return;
    }

    const route = this.routeFor(ref);
    if (!route) {
      return;
    }
    // Nothing on screen could serve this, so hold it: routing lands on the
    // project, and what was asked for — a service selected, a machine opened —
    // is a step past that which only the arriving page can take.
    if (allowDefer && !handler) {
      this.pending = ref;
    }
    this.router.navigate(route);
  }

  /** Resolves an entity to a route, or does nothing if it has no screen of its
   *  own. Hazards and checklist steps land here: they live on screens keyed by
   *  ids the assistant never holds, so they refresh in place instead. */
  private navigateTo(ref: HostEntityRef): void {
    void this.dispatch(ref);
  }

  private routeFor(ref: HostEntityRef): string[] | null {
    switch (ref.entity) {
      case 'project':
        return ref.id ? ['/project-management', 'project-detail', ref.id] : null;
      // Services and roadmaps have no page of their own — both are read from the
      // project detail screen.
      case 'service':
      case 'roadmap':
        return ref.projectId ? ['/project-management', 'project-detail', ref.projectId] : null;
      // Also the project screen, despite machine-info being a real route: that
      // page needs the machine's whole record and a source=project query param
      // to render, and only the project screen holds the list to pull it from.
      // Going there directly with just an id produces an empty page, so route
      // to the project and let its handler finish the open.
      case 'machine':
        return ref.projectId ? ['/project-management', 'project-detail', ref.projectId] : null;
      case 'report':
        return ['/view-ra-reports'];
      default:
        return null;
    }
  }

  /** Short, low-ceremony notice. Not an error, so it stays out of the way. */
  private announce(message: string): void {
    this.toastService.presentToast(message, 3000, AppConstants.SUCCESS_SNACKBAR, 'top');
  }
}
